import { extendObservable, runInAction } from 'mobx';

import { NumberHelper } from '../helpers/NumberHelper';
import { Logger } from '../helpers/Logger';
import { Address } from '../models/Address';
import { Product } from '../models/Product';
import { PURCHASE_TYPES } from '../models/PaymentType';
import { CheckoutController } from '../networking/controllers/CheckoutController';
import { AddressStore } from './AddressStore';
import { CartStore } from './CartStore';
import { SessionStore } from './SessionStore';
import { ApiError, ERROR_STATUSES, ERROR_CODES } from '../models/ApiError';

const CHECKOUT_STAGES = {
  CHOOSE_ADDRESS: 'choose-address',
  SHIPPING_OPTIONS: 'shipping-options',
  INSTALLATION_OPTIONS: 'installation-options',
  BILLING_OPTIONS: 'billing-options',
  CONFIRM_CHECKOUT: 'confirm-checkout',
  CHECKOUT_SUCCESS: 'checkout-success',
};

class CheckoutStoreClass {
  static cloneCurrentCart() {
    return CartStore.cart.toJS().map(item => ({
      amount: item.amount,
      product: new Product(item.product),
      serviceAmount: item.serviceAmount,
    }));
  }

  static getDataCondition() {
    let data = null;
    // eslint-disable-next-line no-restricted-syntax
    for (const item of CartStore.cart.toJS()) {
      if (item && item.product && item.product.condition) {
        if (data) {
          data[item.product.name] = item.product.condition;
        } else {
          data = {};
          data[item.product.name] = item.product.condition;
        }
      }
    }
    return data;
  }

  constructor() {
    extendObservable(this, {
      statusCheckout: undefined,
      additionalNotes: null,
      billingData: null,
      checkout: null,
      checkoutCart: null,
      checkoutCartTotalPrice: null,
      checkoutCartTotalPriceString: null,
      creationError: null,
      creationLoading: null,
      creationStatusLoading: null,
      finishedCheckout: null,
      pickupChosen: false,
      selectedAddress: null,
      firstPhone: null,
      paymentType: null,
      secondPhone: null,
      selectedOption: null,
      selectedTimeSlot: null,
      selectedAgency: null,
      selectedInstallationOptions: null,
      productWithCondition: null,
      billingBase64: null,
      get fullPrice() {
        let optionPrice = 0;
        if (this.selectedOption) {
          optionPrice = this.selectedOption.price;
        }
        if (this.selectedAgency) {
          optionPrice += this.selectedAgency.extraCost;
        }
        return this.checkoutCartTotalPrice + optionPrice;
      },
      get fullPriceString() {
        return NumberHelper.localizeNumber(this.fullPrice);
      },
      get purchaseType() {
        if (this.pickupChosen) {
          return PURCHASE_TYPES.PICKUP;
        }
        return PURCHASE_TYPES.SHIPPING;
      },
      get cartHasInstallations() {
        return this.checkoutCart.some(item => item.serviceAmount &&
          item.product.addedService === 0);
      },
      checkouts: null,
      getError: false,
      get actualCheckouts() {
        let actualCheckouts = null;
        if (this.checkouts !== null) {
          actualCheckouts = this.checkouts.filter(checkout => checkout.isStillOpen());
        }
        return actualCheckouts;
      },
      get pastCheckouts() {
        let pastCheckouts = null;
        if (this.checkouts !== null) {
          pastCheckouts = this.checkouts.filter(checkout => !checkout.isStillOpen());
        }
        return pastCheckouts;
      },
      cancelSuccess: false,
      cancellingCheckout: false,
      cancelInternalError: false,
      cancelApiError: null,
      cancelError: false,
    });
  }

  startCheckout() {
    if (!CartStore.cart) {
      return;
    }
    runInAction(() => {
      this.productWithCondition = CheckoutStoreClass.getDataCondition();
      this.checkoutCart = CheckoutStoreClass.cloneCurrentCart();
      this.checkoutCartTotalPrice = CartStore.cartTotalPrice;
      this.checkoutCartTotalPriceString = CartStore.cartTotalPriceString;
    });
  }

  updateCheckout() {
    if (!CartStore.cart) {
      return;
    }
    runInAction(() => {
      this.checkoutCart = CheckoutStoreClass.cloneCurrentCart();
      this.checkoutCartTotalPrice = CartStore.cartTotalPrice;
      this.checkoutCartTotalPriceString = CartStore.cartTotalPriceString;
    });
  }

  useAddress(addressId) {
    if (!AddressStore.addresses) {
      return;
    }
    const address = AddressStore.addresses.find(addr => addr.id === addressId);
    if (!address) {
      return;
    }
    // Clone address to avoid issues with observables
    runInAction(() => {
      this.selectedAddress = new Address(address);
      this.pickupChosen = false;
    });
  }

  selectPickup() {
    runInAction(() => {
      this.pickupChosen = true;
      this.selectedAddress = null;
    });
  }

  useShipping(firstPhone, secondPhone, selectedOption, selectedTimeSlot, selectedAgency) {
    runInAction(() => {
      this.firstPhone = firstPhone;
      this.secondPhone = secondPhone;
      this.selectedOption = selectedOption;
      this.selectedTimeSlot = selectedTimeSlot;
      this.selectedAgency = selectedAgency;
    });
  }

  useBillingData(billingData, paymentType, bank) {
    runInAction(() => {
      this.billingData = billingData;
      this.paymentType = paymentType;
      this.bank = bank;
    });
  }

  useInstallationOptions(installationOptions) {
    runInAction(() => {
      this.selectedInstallationOptions = installationOptions;
    });
  }

  async confirmCheckout(additionalNotes) {
    runInAction(() => {
      this.creationError = null;
      this.additionalNotes = additionalNotes;
      this.creationLoading = true;
    });
    try {
      const checkout = await CheckoutController.create({
        additionalNotes: this.additionalNotes,
        bank: this.bank,
        addressId: this.selectedAddress ? this.selectedAddress.id : null,
        agency: this.selectedAgency || null,
        billingDataId: this.billingData.id,
        cart: this.checkoutCart,
        firstPhone: this.firstPhone,
        paymentType: this.paymentType,
        secondPhone: this.secondPhone,
        shippingInfo: this.selectedOption,
        timeSlot: this.selectedTimeSlot || null,
        totalPrice: this.fullPrice,
        installationOptions: this.selectedInstallationOptions,
        userId: SessionStore.user.id,
      });
      runInAction(() => {
        this.finishedCheckout = checkout;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.creationError = error;
      });
    } finally {
      runInAction(() => {
        this.creationLoading = false;
      });
    }
  }

  async attempPay(checkoutId, bankId) {
    runInAction(() => {
      this.creationError = null;
      this.creationLoading = true;
    });
    try {
      const checkout = await CheckoutController.attempPay(checkoutId, bankId);
      runInAction(() => {
        this.checkout = checkout;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.creationError = error;
      });
    } finally {
      runInAction(() => {
        this.creationLoading = false;
      });
    }
  }

  async getCheckout(idTransaction) {
    runInAction(() => {
      this.checkout = null;
      this.getError = false;
      this.creationLoading = true;
    });
    try {
      const checkout = await CheckoutController.findByTransaction(idTransaction);
      runInAction(() => {
        this.checkout = checkout;
        this.statusCheckout = null;
        this.creationLoading = false;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.getError = error;
        this.creationLoading = false;
      });
    }
  }

  async getStatusCheckout(idTransaction) {
    runInAction(() => {
      this.checkout = null;
      this.getError = false;
      this.creationStatusLoading = true;
    });
    try {
      const checkoutStatus = await CheckoutController.findStatusByTransaction(idTransaction);
      runInAction(() => {
        this.statusCheckout = checkoutStatus;
        this.creationStatusLoading = false;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.creationStatusLoading = false;
      });
    }
  }

  async getCheckouts() {
    runInAction(() => {
      this.checkouts = null;
      this.getError = false;
    });
    try {
      let checkouts = await CheckoutController.all();
      checkouts = checkouts.sort((a, b) => b.date.ts - a.date.ts);
      runInAction(() => {
        this.checkouts = checkouts;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.getError = true;
      });
    }
  }

  async cancelCheckout(checkoutId) {
    runInAction(() => {
      this.cancellingCheckout = true;
      this.cancelInternalError = false;
      this.cancelApiError = null;
      this.cancelError = false;
      this.cancelSuccess = false;
    });
    try {
      const canceledCheckout = await CheckoutController.cancel(checkoutId);
      runInAction(() => {
        const filteredCheckouts = this.checkouts.filter(checkout => checkout.id !== checkoutId);
        filteredCheckouts.push(canceledCheckout);
        this.checkouts = filteredCheckouts;
        this.cancelSuccess = true;
      });
    } catch (error) {
      Logger.warn(error);
      if (ApiError.isSameTypeAs(error)) {
        if (error.status >= ERROR_STATUSES.SERVER_ERROR) {
          runInAction(() => {
            this.cancelInternalError = true;
          });
        } else if (error.code === ERROR_CODES.INVALID_CHECKOUT_PARAMS ||
            error.code === ERROR_CODES.CHECKOUT_BILLED_OR_CANCELED) {
          runInAction(() => {
            this.cancelApiError = error.code;
          });
        } else {
          runInAction(() => {
            this.cancelError = true;
          });
        }
      }
    } finally {
      runInAction(() => {
        this.cancellingCheckout = false;
      });
    }
  }

  async getBillingPDF(idBilling) {
    runInAction(() => {
      this.checkout = null;
      this.getError = false;
      this.creationLoading = true;
    });
    try {
      const data = await CheckoutController.findBillingPDF(idBilling);
      runInAction(() => {
        this.billingBase64 = data ? data.base64 : null;
        this.creationLoading = false;
      });
    } catch (error) {
      Logger.warn(error);
      runInAction(() => {
        this.getError = error;
        this.creationLoading = false;
      });
    }
  }

  clearPhoneData() {
    runInAction(() => {
      this.firstPhone = null;
      this.secondPhone = null;
    });
  }

  clearBillingData() {
    runInAction(() => {
      this.billingData = null;
      this.paymentType = null;
    });
  }

  clearShippingOptionData() {
    runInAction(() => {
      this.selectedOption = null;
      this.selectedTimeSlot = null;
      this.selectedAgency = null;
    });
  }

  clearInstallationData() {
    runInAction(() => {
      this.selectedInstallationOptions = null;
    });
  }

  clearAddressData() {
    runInAction(() => {
      this.selectedAddress = null;
      this.pickupChosen = false;
    });
  }

  clearCreationError() {
    runInAction(() => {
      this.creationError = null;
    });
  }

  clearCancelErrors() {
    runInAction(() => {
      this.cancelInternalError = false;
      this.cancelApiError = null;
      this.cancelError = false;
    });
  }

  clearCancelSuccess() {
    runInAction(() => {
      this.cancelSuccess = false;
    });
  }

  clearMyOrdersObservables() {
    runInAction(() => {
      this.getError = false;
      this.cancelInternalError = false;
      this.cancelApiError = null;
      this.cancelError = false;
      this.cancellingCheckout = false;
      this.cancelSuccess = false;
    });
  }
}

const CheckoutStore = new CheckoutStoreClass();

export { CheckoutStore, CHECKOUT_STAGES };



// WEBPACK FOOTER //
// ./src/stores/CheckoutStore.js